import React, { useState } from "react";
import { TextInput } from "flowbite-react";
import { useTranslation } from "react-i18next";
import { CustomButton } from "../atoms/CustomButton";
import { ModalShell } from "../atoms/ModalShell";
import { HiGift } from "react-icons/hi";
import { useApplyPromoCode } from "../../hooks/promoCode";
import useAuth from "../../hooks/useAuth";
import { usePaymentVerification } from "../../hooks/usePaymentVerification";

export interface PromoCodeModalProps {
  showModal: boolean;
  setShowModal: (value: boolean) => void;
  onSubmit?: () => void;
}

export default function PromoCodeModal({
  showModal,
  setShowModal,
  onSubmit,
}: PromoCodeModalProps) {
  const { t } = useTranslation();
  const [promoCode, setPromoCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { mutateAsync: applyPromoCode } = useApplyPromoCode();
  const { customer_identification, user } = useAuth();
  const { forcePaymentCheck } = usePaymentVerification();

  const handleSubmit = async () => {
    if (!promoCode.trim()) {
      setError(t("dashboard.promoCode.errorEmpty") as string);
      return;
    }

    setError(null);
    setIsLoading(true);

    try {
      await applyPromoCode({
        customerIdentification: customer_identification,
        entityIdentification: user.entity_identification,
        promoCode,
      });

      await forcePaymentCheck();

      onSubmit?.();
      setShowModal(false);
      setPromoCode("");
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        (t("dashboard.promoCode.errorInvalid") as string);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setShowModal(false);
    setPromoCode("");
    setError(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !isLoading) {
      handleSubmit();
    }
  };

  return (
    <ModalShell
      show={showModal}
      onClose={handleClose}
      size="md"
      footer={
        <>
          <CustomButton
            onClick={handleClose}
            disabled={isLoading}
            title={t("common.cancel") as string}
          />
          <CustomButton
            variant="primary"
            onClick={handleSubmit}
            disabled={isLoading || !promoCode.trim()}
            isLoading={isLoading}
            title={t("dashboard.promoCode.applyButton") as string}
          />
        </>
      }
    >
      <div className="flex flex-col items-center gap-3 text-center">
        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
          <HiGift size={24} className="text-blue-600" />
        </div>

        <div className="flex flex-col gap-2">
          <div className="text-gray-900 text-lg font-semibold leading-7">
            {t("dashboard.promoCode.title")}
          </div>
          <div className="text-gray-500 text-sm leading-tight">
            {t("dashboard.promoCode.description")}
          </div>
        </div>

        <div className="w-full">
          <TextInput
            id="promo-code"
            type="text"
            placeholder={t("dashboard.promoCode.placeholder") as string}
            value={promoCode}
            onChange={(e) => {
              const code = e.target.value.toUpperCase();
              setPromoCode(code);
              setError(null);
            }}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
            color={error ? "failure" : undefined}
            theme={{
              field: {
                input: { withShadow: { off: "text-center uppercase" } },
              },
            }}
          />
          {error && (
            <p className="mt-2 text-sm text-red-600 text-left">{error}</p>
          )}
        </div>
      </div>
    </ModalShell>
  );
}
